import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PreviewStatus, VideoClip } from 'services/VideoEditingService';
import { sourceProviderLabel } from './sourceProvider';
import CachedRangePreviewFrame, { sourceThumbFrame } from 'pages/VideoEditing/components/CachedRangePreviewFrame';
import { formatDuration } from 'pages/VideoEditing/format';

interface AddClipCardProps {
  clip: VideoClip;
  status: PreviewStatus | undefined;
  previewError?: string;
  // How many library entries this file already has. 0 = not in the collection yet.
  existingCount: number;
  selected: boolean;
  // False while the preview is still being prepared, or when there's no usable duration.
  selectable: boolean;
  onToggle: (fileId: string) => void;
  onAdd: (clip: VideoClip) => void;
  onRetryPreview: (clip: VideoClip) => void;
}

/**
 * One clip in a source folder — Google Drive or Dropbox — offered to the library.
 *
 * A clip is only addable once its preview transcode is done: the stream proxy refuses a video
 * that has none, so there would be nothing to trim against and the entry would be filed
 * without a poster or a sound measurement. Rather than let that happen and half-explain it
 * afterwards, the card simply says what it's waiting for.
 */
export default function AddClipCard({
  clip, status, previewError, existingCount, selected, selectable,
  onToggle, onAdd, onRetryPreview,
}: AddClipCardProps) {
  const ready = status === 'ready';
  const failed = status === 'error';

  return (
    <Paper
      variant="outlined"
      // The whole card opens the add dialog, not just the button — the card IS the clip, and
      // hunting for a small target under every thumbnail is a poor way to work through a
      // folder. The button stays as the labelled affordance for keyboard and screen readers;
      // the controls that live on top of the card stop the click reaching here.
      onClick={ready ? () => onAdd(clip) : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderColor: selected ? 'primary.main' : undefined,
        borderWidth: selected ? 2 : 1,
        cursor: ready ? 'pointer' : 'default',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        ...(ready && {
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: '0 6px 16px rgba(15, 23, 42, 0.10)',
          },
        }),
      }}
    >
      <Box
        sx={{
          position: 'relative', backgroundColor: '#0F172A', aspectRatio: '9 / 16',
          maxHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {ready ? (
          <CachedRangePreviewFrame frame={sourceThumbFrame(clip)} loadEnabled />
        ) : (
          <Box sx={{ textAlign: 'center', px: 1 }}>
            {failed ? (
              <>
                <Typography variant="caption" sx={{ color: '#FCA5A5', display: 'block' }}>
                  {previewError || 'Preview failed'}
                </Typography>
                <Button
                  size="small"
                  startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                  onClick={(e) => { e.stopPropagation(); onRetryPreview(clip); }}
                  sx={{ color: '#E2E8F0', mt: 0.5 }}
                >
                  Retry
                </Button>
              </>
            ) : (
              <>
                <CircularProgress size={18} sx={{ color: '#94A3B8' }} />
                <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mt: 0.5 }}>
                  Preparing preview…
                </Typography>
              </>
            )}
          </Box>
        )}

        <Checkbox
          size="small"
          checked={selected}
          disabled={!selectable}
          onChange={() => onToggle(clip.fileId)}
          // Ticking a clip for the bulk add must not also open the dialog for it.
          onClick={e => e.stopPropagation()}
          inputProps={{ 'aria-label': `Select ${clip.name}` }}
          sx={{
            position: 'absolute', top: 2, left: 2, color: '#E2E8F0',
            '&.Mui-checked': { color: '#818CF8' },
            '&.Mui-disabled': { color: '#475569' },
          }}
        />

        {/* Which service the file actually lives on. Only shown for the non-Drive ones: a
            badge on every card in a Drive folder would say nothing. */}
        {sourceProviderLabel(clip.fileId) && (
          <Chip
            size="small"
            label={sourceProviderLabel(clip.fileId)}
            sx={{
              position: 'absolute', bottom: 6, left: 6, height: 20, fontSize: 11,
              backgroundColor: 'rgba(15,23,42,0.75)', color: '#E2E8F0',
            }}
          />
        )}

        {existingCount > 0 && (
          <Tooltip title={`This clip already has ${existingCount} entr${existingCount === 1 ? 'y' : 'ies'} in the library. Adding again creates another one.`}>
            <Chip
              size="small"
              label={existingCount === 1 ? 'In library' : `In library (${existingCount})`}
              sx={{
                position: 'absolute', top: 6, right: 6, height: 20, fontSize: 11,
                backgroundColor: 'rgba(15,23,42,0.75)', color: '#E2E8F0',
              }}
            />
          </Tooltip>
        )}
      </Box>

      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5, flexGrow: 1 }}>
        <Tooltip title={clip.name}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', lineHeight: 1.3 }} noWrap>
            {clip.name}
          </Typography>
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          {clip.durationSec != null ? formatDuration(clip.durationSec) : 'duration unknown'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          size="small"
          fullWidth
          variant="outlined"
          startIcon={<AddIcon fontSize="small" />}
          // The card behind it does the same thing, so this only needs to not double-fire.
          onClick={(e) => { e.stopPropagation(); onAdd(clip); }}
          disabled={!ready}
          sx={{ mt: 0.5, justifyContent: 'center' }}
        >
          Add…
        </Button>
      </Box>
    </Paper>
  );
}
